import React, { useEffect, useState } from 'react';
import Toggle from '../../Shared/Toggle/toggle';
import List from '../../Shared/List/List';
import findingTypesApi from '../../../utils/api/findingTypes';
import FindingPriorityOptions from './FindingPriorityOptions';
import { GithubPicker } from 'react-color';
import { Modal } from '@stormwater-software/lib-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Prompt } from 'react-router-dom';
import templatePriorityOptionsApi, {
  TemplatePriorityOptionType,
} from '../../../utils/api/templatePriorityOptionsApi';
import inspectionTemplateApi from '../../../utils/api/inspectionTemplate';
import ClearComplianceByProjectModal from './ClearComplianceByProjectModal';
import {
  FormContextProvider,
  SchemaFieldsWithContextUI,
} from '@stormwater-software/lib-form';
import { FormModal } from '../../Shared/form';

type InspectionDetails = {
  name: string;
  id: number;
  details: {
    inspection_template: {
      id: number;
      name: string;
    };
    is_enabled: boolean;
    is_required_for_cert: boolean;
  };
};

const findingSchema = {
  name: {
    label: 'Name',
    validation: {
      required: true,
    },
  },
};

export default function InspectionTemplateFinding(props: {
  templateId?: number;
  name?: string;
}) {
  const [basicFindings, setBasicFindings] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [toggleError, setToggleError] = useState('');
  const [createModal, setCreateModal] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<false | number>(false);
  const [deleteModal, setDeleteModal] = useState<false | number>(false);
  const [colorSetter, setColorSetter] = useState<any>({ 1: '#2FF363' });
  const [tempArr, setTempArr] = useState<any[]>([]);
  const [findingPriorityOptions, setFindingPriorityOptions] = useState<
    TemplatePriorityOptionType[]
  >([]);
  const [isPriorityEnabled, setIsPriorityEnabled] = useState(false);
  const [initialRequiredForCert, setInitialRequiredForCert] = useState<
    Record<number, boolean>
  >({});
  const [affectedProjects, setAffectedProjects] = useState<any[]>([]);
  const [pendingSavePayload, setPendingSavePayload] = useState<any[] | null>(
    null,
  );
  const handleDelete = (id: number) => {
    setErrorMsg('');
    findingTypesApi
      .destroy(id)
      .then(() => {
        hideDeleteModal();
        setBasicFindings(
          basicFindings.filter((ele) => {
            return ele.id !== id;
          }),
        );
      })
      .catch((e) => {
        if (e.response && e.response.data && e.response.data.message) {
          setErrorMsg(e.response.data.message);
        } else if (e.message) {
          setErrorMsg(e.message);
        } else {
          setErrorMsg('An error has occurred');
        }
      });
  };

  const showCreateModal = () => {
    setCreateModal(true);
    setErrorMsg('');
  };

  const showEditModal = (id: number) => {
    setEditModal(id);
    setErrorMsg('');
  };

  const showDeleteModal = (id: number) => {
    setDeleteModal(id);
    setErrorMsg('');
  };

  const hideDeleteModal = () => {
    setDeleteModal(false);
  };

  const header = [
    {
      id: 1,
      head: 'Findings',
    },
    {
      id: 2,
      head: 'Include',
    },
    {
      id: 3,
      head: 'Required for Compliance Certification',
    },
  ];

  useEffect(() => {
    (async () => {
      if (props.templateId) {
        const data = await findingTypesApi.index(props.templateId);

        setBasicFindings(data);
        let newColor: any = {};
        const initialReq: Record<number, boolean> = {};

        for (let i = 0; i < data.length; i++) {
          newColor[data[i].id] = data[i].details.color;
          initialReq[data[i].id] =
            data[i].details.is_required_for_cert === true;
        }
        setColorSetter(newColor);
        setInitialRequiredForCert(initialReq);
      }
    })();

    return () => {
      setBasicFindings([]);
    };
  }, []);

  useEffect(() => {
    if (!props.templateId) return;
    inspectionTemplateApi.get(props.templateId).then((data) => {
      setIsPriorityEnabled(data.is_priority_dropdown_enabled);
      templatePriorityOptionsApi
        .getByTemplateId(props.templateId!!)
        .then((data) => {
          setFindingPriorityOptions(data);
        });
    });
  }, [props.templateId]);

  const getInitialEnable = basicFindings.filter((e) => e.details.is_enabled);

  const handleToggle = (id: number, name: string) => {
    const getSelectedFinding: any = basicFindings.filter((e) => e.id === id)[0];

    setBasicFindings(
      basicFindings.map((e) => {
        if (e.id === id) {
          if (
            name === 'is_enabled' &&
            (getInitialEnable.length > 1 || e.details.is_enabled === false)
          ) {
            e.details.is_enabled = !getSelectedFinding.details.is_enabled;
            setToggleError('');
            if (e.details.is_enabled === false) {
              e.details.is_required_for_cert = false;
            }
          } else if (name === 'is_required_for_cert') {
            if (e.details.is_enabled === false) {
              setToggleError('Include must be enabled');
            } else {
              e.details.is_required_for_cert =
                !getSelectedFinding.details.is_required_for_cert;
            }
          } else {
            setToggleError('At least one Include must be enabled');
          }
        }

        return e;
      }),
    );

    const getFilteredFind = basicFindings.filter((e) => e.id === id)[0];

    tempArr.push(getFilteredFind);
  };

  // Aggregate affected projects across every type whose is_required_for_cert was flipped false->true this session.
  const fetchAffectedProjects = async (data: any[]): Promise<any[]> => {
    if (!props.templateId) return [];
    const newlyRequired = data.filter(
      (e) =>
        e?.details?.is_required_for_cert === true &&
        initialRequiredForCert[e.id] === false,
    );

    if (newlyRequired.length === 0) return [];

    const lists = await Promise.all(
      newlyRequired.map((ft) =>
        findingTypesApi
          .getAffectedInspections(ft.id, props.templateId!)
          .catch(() => [] as any[]),
      ),
    );

    return lists.flat().reduce<any[]>((acc, p) => {
      const existing = acc.find((x) => x.project_id === p.project_id);

      if (existing) existing.inspection_count += p.inspection_count;
      else acc.push({ ...p });

      return acc;
    }, []);
  };

  const saveData = async (data: any[]) => {
    const toastId = toast('Saving finding');

    try {
      await findingTypesApi.update(data);
      setTempArr([]);
      setInitialRequiredForCert((prev) => {
        const next = { ...prev };

        data.forEach((e) => {
          if (e?.id != null && e?.details) {
            next[e.id] = e.details.is_required_for_cert === true;
          }
        });

        return next;
      });
      toast.update(toastId, { render: 'Finding saved!', type: 'success' });
    } catch (e) {
      toast.update(toastId, {
        render: 'Failed to save finding',
        type: 'error',
      });
      console.log(e);
    }
  };

  const handleSaveButton = async () => {
    const data = tempArr
      .filter((e, i) => tempArr.indexOf(e) === i)
      .map((e) => ({
        ...e,
        inspection_template_id: props.templateId,
        name: e.name.trim(),
      }));

    const affected = await fetchAffectedProjects(data);

    if (affected.length > 0) {
      setAffectedProjects(affected);
      setPendingSavePayload(data);

      return;
    }
    await saveData(data);
  };

  const handleConfirmClearCompliance = async () => {
    const data = pendingSavePayload;

    setAffectedProjects([]);
    setPendingSavePayload(null);
    if (data) await saveData(data);
  };

  const handleCreateFindingType = async (data: { name: string }) => {
    if (props.templateId && data.name) {
      try {
        await findingTypesApi
          .create({
            inspection_template_id: props.templateId,
            name: data.name.trim(),
          })
          .then((res) => {
            setBasicFindings(basicFindings.concat(res));
            setColorSetter({ ...colorSetter, [res.id]: res.details.color });
            setCreateModal(false);
            setToggleError('');
          })
          .catch(() => {});
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleUpdateFindingType = async (data: {
    id: number;
    name: string;
  }) => {
    if (props.templateId && data.name) {
      const arr = [];

      arr.push(data);
      const getData = arr.map((e: any) => {
        e.inspection_template_id = props.templateId;
        e.name = e.name.trim();

        return e;
      });

      try {
        await findingTypesApi
          .update(getData)
          .then(() => {
            setBasicFindings(
              basicFindings.map((e) => {
                if (e.id === data.id) {
                  e.name = data.name;

                  return e;
                }

                return e;
              }),
            );
            const getFilteredFind = basicFindings.filter(
              (e) => e.id === data.id,
            )[0];

            tempArr.push(getFilteredFind);
            setEditModal(false);
          })
          .catch(() => {});
      } catch (x) {
        console.log(x);
      }
    }
  };

  const handleColorChange = (findingId: number) => async (color: any) => {
    if (props.templateId && color) {
      setBasicFindings(
        basicFindings.map((e) => {
          if (e.id === findingId) {
            setColorSetter((prevState: any) => ({
              ...prevState,
              [findingId]: color.hex,
            }));
            e.details.color = color.hex;

            return e;
          }

          return e;
        }),
      );
    }
    const getFilteredFind = basicFindings.filter((e) => e.id === findingId)[0];

    tempArr.push(getFilteredFind);
  };

  return (
    <>
      <Prompt
        when={tempArr.length > 0}
        message='You have unchanged finding edit. Are you sure you want to exit?'
      />

      {toggleError ? (
        <h5 className='error-messages finding-error'>
          <>
            <em>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              &nbsp;
              {toggleError}
            </em>
          </>
        </h5>
      ) : null}

      <div>
        <button
          className='action-buttons-primary primary'
          style={{ float: 'right' }}
          onClick={() => handleSaveButton()}
        >
          Save
        </button>
        <div>
          <List
            headers={header.map((heading) => ({ label: heading.head }))}
            rows={basicFindings
              .filter(({ is_static_type }) => is_static_type === true)
              .map(({ id, name, details }: InspectionDetails) => {
                return {
                  id: id,
                  cells: [
                    name === 'A'
                      ? 'Achievement'
                      : name === 'MI'
                        ? 'Maintenance Item'
                        : 'Corrective Action',
                    <Toggle
                      isChecked={details.is_enabled}
                      handleChange={() => handleToggle(id, 'is_enabled')}
                    />,
                    <Toggle
                      isChecked={details.is_required_for_cert}
                      handleChange={() =>
                        handleToggle(id, 'is_required_for_cert')
                      }
                    />,
                  ],
                };
              })}
            pageItems=''
          />
        </div>
      </div>

      <div>
        <button
          className='outline create-new contact.buttons'
          onClick={() => showCreateModal()}
        >
          <i className='fa fa-pencil' /> &nbsp;Create New Finding Type
        </button>
      </div>

      <div>
        <List
          headers={header.map((heading) => ({ label: heading.head }))}
          rows={basicFindings
            .filter(({ is_static_type }) => is_static_type === false)
            .map(({ id, name, details }: InspectionDetails) => {
              return {
                id: id,
                cells: [
                  name,
                  <Toggle
                    isChecked={details.is_enabled}
                    handleChange={() => handleToggle(id, 'is_enabled')}
                  />,
                  <Toggle
                    isChecked={details.is_required_for_cert}
                    handleChange={() =>
                      handleToggle(id, 'is_required_for_cert')
                    }
                  />,

                  <div
                    style={{
                      backgroundColor: `${colorSetter[id]}`,
                      borderRadius: '20px',
                      height: '40px',
                      width: '40px',
                      border: '1px solid rgba(56, 89, 128, 0.29)',
                    }}
                  ></div>,

                  <GithubPicker
                    width='230px'
                    onChange={handleColorChange(id)}
                  />,

                  <button
                    className='outline create-new contact.buttons'
                    onClick={() => showEditModal(id)}
                  >
                    <i className={'fa fa-pencil'} />
                  </button>,
                  <button
                    className='icon-only-button outline warn'
                    onClick={() => showDeleteModal(id)}
                  >
                    <i className='fa fa-trash' />
                  </button>,
                ],
              };
            })}
          pageItems=''
        />
      </div>

      <div className='finding-priority-container'>
        <div className='finding-priority-header'>
          <h3>Finding Priority Group Options</h3>
          <Toggle
            isChecked={isPriorityEnabled}
            handleChange={() => {
              setIsPriorityEnabled(!isPriorityEnabled);
              inspectionTemplateApi
                .update(props.templateId, {
                  is_priority_dropdown_enabled: !isPriorityEnabled,
                })
                .then(() => {
                  toast.success(
                    `Finding priority ${
                      isPriorityEnabled ? 'disabled' : 'enabled'
                    } successfully!`,
                  );
                })
                .catch(() => {
                  toast.error('Something went wrong!');
                });
            }}
          />
        </div>
        {isPriorityEnabled && (
          <div className='finding-priority-options'>
            <FindingPriorityOptions
              templateId={props.templateId}
              findingPriorityOptions={findingPriorityOptions}
            />
          </div>
        )}
      </div>

      {createModal && (
        <FormContextProvider>
          <FormModal
            onCancel={() => setCreateModal(false)}
            onSubmit={handleCreateFindingType}
            modalProps={{
              title: 'Create New Finding',
            }}
          >
            <SchemaFieldsWithContextUI
              schema={findingSchema}
              initialFormData={{
                name: '',
              }}
            />
          </FormModal>
        </FormContextProvider>
      )}

      {editModal && (
        <FormContextProvider>
          <FormModal
            onCancel={() => setEditModal(false)}
            onSubmit={handleUpdateFindingType}
            modalProps={{
              title: 'Edit Finding',
            }}
          >
            <SchemaFieldsWithContextUI
              schema={findingSchema}
              initialFormData={{
                id: editModal,
                name: basicFindings.find((e) => e.id === editModal)?.name ?? '',
              }}
            />
          </FormModal>
        </FormContextProvider>
      )}

      {affectedProjects.length > 0 && (
        <ClearComplianceByProjectModal
          projects={affectedProjects}
          onConfirm={handleConfirmClearCompliance}
          onClose={() => {
            setAffectedProjects([]);
            setPendingSavePayload(null);
          }}
        />
      )}

      <Modal
        handleSubmit={() => handleDelete(+deleteModal)}
        show={Boolean(deleteModal)}
        handleClose={() => setDeleteModal(false)}
        errorMsg={errorMsg}
        title={`Delete Finding`}
        hideButtons={true}
      >
        <h3>Warning: This cannot be undone</h3>
        <p>
          <>
            Are you sure that you want to delete the <strong></strong> Finding
            Type?
          </>
        </p>
        <div className='buttons'>
          <button
            className='reversed outline'
            type='button'
            onClick={hideDeleteModal}
          >
            Cancel
          </button>
          &nbsp;
          <button
            className='warn reversed'
            onClick={() => handleDelete(+deleteModal)}
          >
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
}
